/* global Office */
import { useState, useEffect, useRef } from "react";
import { useLicenseContext } from "../../contexts/LicenseContext";
import { useI18n } from "../../contexts/I18nContext";
import {
  getSecurityState,
  getUnlockMaterial,
  setupUserKey,
  rotateUserKey,
  resetPasswordManager,
} from "../../api/passwordManagerSecurityApi";
import {
  activatePasswordManager,
  updateEmail,
} from "../../api/passwordManagerApi";
import {
  getUnreadNotifications,
  markAsRead,
  markAllAsRead,
} from "../../api/notificationApi";
import {
  getActiveDbSummary,
  getRecipientPasswords,
  getUsersForRotation,
  rotateMasterKey,
} from "../../api/passwordDatabaseApi";
import * as crypto from "../../utils/crypto";

/**
 * Security Page (Page 1 - Sicherheit & Zugriff)
 * Handles 4 states:
 * 1. Password Manager Deactivated
 * 2. User Key Setup (first time)
 * 3. Unlock (enter password)
 * 4. Unlocked (success state)
 */
export default function SecurityPage({
  oid: oidProp,
  onUnlockSuccess,
  onNavigate,
  isUnlockedFromParent,
  passwortDatenbankId,
  masterKey,
  masterKeyVersion,
  isOwner,
  onMasterKeyRotated,
  onConflictDetected,
}) {
  const { t, formatDateTime } = useI18n();
  const { oid: oidFromContext } = useLicenseContext();
  // In dialog mode the OID comes from useDialogAuth (via prop).
  // In taskpane / dev mode it comes from LicenseContext.
  const oid = oidProp || oidFromContext;

  // Email prefill: dialog mode → URL param; taskpane/dev → Office.js direct read
  const userEmail = new URLSearchParams(window.location.search).get("userEmail")
    || (typeof Office !== "undefined" && Office?.context?.mailbox?.userProfile?.emailAddress)
    || null;

  // Security state from backend
  const [securityState, setSecurityState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form states
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [agreedToWarning, setAgreedToWarning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Local unlock state (initialized from parent so it persists across page navigations)
  const [isUnlocked, setIsUnlocked] = useState(isUnlockedFromParent || false);

  // Password rotation states
  const [showRotateForm, setShowRotateForm] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [rotationSuccess, setRotationSuccess] = useState(false);

  // Reset confirmation modal state
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);

  // MasterKey rotation states
  const [showRotateConfirmModal, setShowRotateConfirmModal] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateSuccess, setRotateSuccess] = useState(false);

  // Refs for auto-focus
  const oldPasswordRef = useRef(null);
  const emailDraftRef = useRef(null);

  // Email states - prefill from Office.js userEmail if available
  const [email, setEmail] = useState(userEmail || ""); // Email for first-time setup
  const [editingEmail, setEditingEmail] = useState(false); // Editing email in Konto section
  const [emailDraft, setEmailDraft] = useState(""); // Draft value while editing
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Auto-focus old password input when rotation form is shown
  useEffect(() => {
    if (showRotateForm && oldPasswordRef.current) {
      oldPasswordRef.current.focus();
    }
  }, [showRotateForm]);

  // Auto-focus email input when editing email
  useEffect(() => {
    if (editingEmail && emailDraftRef.current) {
      emailDraftRef.current.focus();
    }
  }, [editingEmail]);

  // Load security state on mount
  useEffect(() => {
    if (oid) {
      loadSecurityState();
    }
  }, [oid]);

  // Prefill email from Office.js when userEmail becomes available
  // Only prefill if email is empty (user hasn't typed anything yet)
  useEffect(() => {
    if (userEmail && !email) {
      setEmail(userEmail);
    }
  }, [userEmail]);

  const loadSecurityState = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getSecurityState(oid);
      if (response.success) {
        setSecurityState(response.data);
        // Prefill email from backend (if available)
        if (response.data?.email) {
          setEmail(response.data.email);
        }
      } else {
        setError(response.message || t("Fehler beim Laden des Status"));
      }
    } catch (err) {
      console.error("Error loading security state:", err);
      setError(t("Fehler beim Laden des Sicherheitsstatus"));
    } finally {
      setLoading(false);
    }
  };

  // Load unread notifications (returns the fetched array so callers can inspect it)
  const loadNotifications = async () => {
    if (!oid) return [];
    setNotificationsLoading(true);
    try {
      const response = await getUnreadNotifications(oid);
      if (response.success) {
        const items = response.data || [];
        setNotifications(items);
        return items;
      }
    } catch (err) {
      console.error("Error loading notifications:", err);
    } finally {
      setNotificationsLoading(false);
    }
    return [];
  };

  // Mark single notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await markAsRead(notificationId, oid);
      if (response.success) {
        setNotifications((prev) =>
          prev.filter((n) => n.benachrichtigungId !== notificationId),
        );
      }
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // Mark all notifications as read
  const handleMarkAllAsRead = async () => {
    try {
      const response = await markAllAsRead(oid);
      if (response.success) {
        setNotifications([]);
      }
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  // Get notification type styling
  const getNotificationStyle = (typ) => {
    switch (typ) {
      case "ZugriffsanfrageGenehmigt":
        return {
          icon: "✅",
          color: "#155724",
          bgColor: "#d4edda",
          borderColor: "#28a745",
        };
      case "ZugriffsanfrageAbgelehnt":
        return {
          icon: "❌",
          color: "#721c24",
          bgColor: "#f8d7da",
          borderColor: "#dc3545",
        };
      case "ZugriffEntzogen":
        return {
          icon: "🚫",
          color: "#721c24",
          bgColor: "#f8d7da",
          borderColor: "#dc3545",
        };
      case "ZugriffBeendet":
        return {
          icon: "ℹ️",
          color: "#0c5460",
          bgColor: "#d1ecf1",
          borderColor: "#17a2b8",
        };
      case "MasterKeyGeaendert":
        return {
          icon: "🔑",
          color: "#0c5460",
          bgColor: "#d1ecf1",
          borderColor: "#17a2b8",
        };
      default:
        return {
          icon: "📢",
          color: "#383d41",
          bgColor: "#e2e3e5",
          borderColor: "#6c757d",
        };
    }
  };

  // Handle user key setup (first time)
  const handleSetupUserKey = async (e) => {
    e.preventDefault();

    // Validation
    if (!email || !email.trim()) {
      setError(t("Bitte geben Sie Ihre E-Mail-Adresse ein"));
      return;
    }
    if (!password || !passwordConfirm) {
      setError(t("Bitte füllen Sie alle Felder aus"));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t("Passwörter stimmen nicht überein"));
      return;
    }
    if (password.length < 8) {
      setError(t("Benutzer-Schlüssel muss mindestens 8 Zeichen lang sein"));
      return;
    }
    if (!agreedToWarning) {
      setError(t("Bitte bestätigen Sie, dass Sie die Warnung verstanden haben"));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Step 1: Generate RSA keypair
      console.log("[Crypto] Generating RSA keypair...");
      const { publicKey, privateKey } = await crypto.generateRSAKeypair();

      // Step 2: Encrypt private key with user password (Benutzer-Schlüssel)
      console.log("[Crypto] Encrypting private key with password...");
      const { encryptedPrivateKey, envelope: privateKeyEnvelope } =
        await crypto.encryptPrivateKeyWithPassword(privateKey, password);

      console.log("[Crypto] Keypair generated successfully");
      console.log("[Crypto] KDF:", privateKeyEnvelope.kdf.algorithm);

      // Step 3: Generate MasterKey for password database
      console.log("[Crypto] Generating MasterKey...");
      const masterKey = crypto.generateMasterKey();

      // Step 4: Encrypt MasterKey with own public key
      console.log("[Crypto] Encrypting MasterKey with public key...");
      const encryptedMasterKey = await crypto.encryptMasterKeyWithPublicKey(
        masterKey,
        publicKey,
      );

      // Step 5: Create MasterKeyEnvelope
      const masterKeyEnvelope = crypto.createMasterKeyEnvelope(1, 1);
      console.log("[Crypto] MasterKey encrypted successfully");

      // Step 6: Save user key (PublicKey + EncryptedPrivateKey)
      const userKeyRequest = {
        publicKey: publicKey,
        encryptedPrivateKey: encryptedPrivateKey,
        privateKeyEnvelope: privateKeyEnvelope, // Pass as object, API will serialize
      };

      const setupResponse = await setupUserKey(oid, userKeyRequest);

      if (!setupResponse.success) {
        setError(
          setupResponse.message || t("Fehler beim Einrichten des Schlüssels"),
        );
        return;
      }

      console.log("[Crypto] User key saved successfully");

      // Step 7: Activate password manager (create PasswortDatenbank + MasterKey)
      const activateRequest = {
        oid: oid,
        email: email.trim(),
        publicKey: publicKey,
        encryptedPrivateKey: encryptedPrivateKey,
        encryptedMasterKey: encryptedMasterKey,
        masterKeyEnvelope: JSON.stringify(masterKeyEnvelope),
      };

      const activateResponse = await activatePasswordManager(activateRequest);

      if (activateResponse.success) {
        console.log("[Crypto] Password manager activated successfully");

        // Reload security state
        await loadSecurityState();
        setPassword("");
        setPasswordConfirm("");
        setAgreedToWarning(false);
      } else {
        setError(
          activateResponse.message || t("Fehler beim Aktivieren der Passwortdatenbank"),
        );
      }
    } catch (err) {
      console.error("Error setting up user key:", err);

      // Check for crypto-specific errors
      if (err.message && err.message.includes("Failed to generate")) {
        setError(
          t("Fehler bei der Schlüsselgenerierung. Bitte versuchen Sie es erneut."),
        );
      } else if (err.message && err.message.includes("Failed to encrypt")) {
        setError(
          t("Fehler bei der Verschlüsselung. Bitte versuchen Sie es erneut."),
        );
      } else {
        setError(t("Fehler beim Einrichten des Benutzer-Schlüssels"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle unlock – delegates crypto to sidebar via bridge
  const handleUnlock = async (e) => {
    e.preventDefault();

    if (!password) {
      setError(t("Bitte geben Sie Ihr Passwort ein"));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Import bridge dynamically (only available in dialog context)
      const { sendRequest } = await import("../../services/dialogBridge");
      const { OPS } = await import("../../services/messageOps");

      // Send UNLOCK_REQUEST to sidebar – sidebar performs all crypto
      console.log("[SecurityPage] Sending UNLOCK_REQUEST to sidebar...");
      await sendRequest(OPS.UNLOCK_REQUEST, { password });
      console.log("[SecurityPage] Sidebar unlock succeeded");

      // Fetch updated state from sidebar
      const state = await sendRequest(OPS.GET_STATE);
      console.log("[SecurityPage] Got state from sidebar:", state);

      // Fetch MasterKey bytes from sidebar so dialog pages can use them
      let decryptedMasterKey = null;
      try {
        const mkResult = await sendRequest(OPS.DECRYPT_MASTER_KEY);
        if (mkResult?.masterKeyBase64) {
          decryptedMasterKey = Uint8Array.from(atob(mkResult.masterKeyBase64), c => c.charCodeAt(0));
        }
      } catch (mkErr) {
        console.warn("[SecurityPage] Failed to get MasterKey from sidebar:", mkErr);
      }

      // Reload security state from backend
      await loadSecurityState();

      setIsUnlocked(true);

      // Notify parent dialog with the state from sidebar
      onUnlockSuccess(
        state.passwortDatenbankId,
        state.activeUserCount || 0,
        decryptedMasterKey,
        state.isPaid || false,
        state.isOwner !== false,
        state.masterKeyVersion || 1,
      );
      setPassword("");

      // Load notifications after successful unlock — only navigate if none exist
      const items = await loadNotifications();
      if (items.length === 0) {
        if (onNavigate) onNavigate("passwords");
      }
    } catch (err) {
      console.error("Error unlocking:", err);

      // Check for known error codes from bridge
      if (err.errorCode === "UNLOCK_FAILED") {
        setError(err.message || t("Falsches Passwort. Bitte versuchen Sie es erneut."));
      } else if (err.message && err.message.includes("timed out")) {
        setError(t("Zeitüberschreitung beim Entsperren. Bitte versuchen Sie es erneut."));
      } else if (err.message && err.message.includes("Falsches Passwort")) {
        setError(t("Falsches Passwort. Bitte versuchen Sie es erneut."));
      } else {
        setError(t("Fehler beim Entsperren der Datenbank"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle password rotation (change Benutzer-Schlüssel)
  const handleRotatePassword = async (e) => {
    e.preventDefault();

    // Validation
    if (!oldPassword || !newPassword || !newPasswordConfirm) {
      setError(t("Bitte füllen Sie alle Felder aus"));
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError(t("Neue Benutzer-Schlüssel stimmen nicht überein"));
      return;
    }
    if (newPassword.length < 8) {
      setError(t("Neuer Benutzer-Schlüssel muss mindestens 8 Zeichen lang sein"));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Step 1: Get current unlock material
      const response = await getUnlockMaterial(oid);
      if (!response.success) {
        setError(response.message || t("Fehler beim Laden der Schlüssel"));
        return;
      }

      const { encryptedPrivateKey, privateKeyEnvelope } = response.data;

      console.log("[Crypto] Rotating user key...");

      // Step 2: Decrypt private key with OLD password (verification)
      console.log("[Crypto] Verifying old password...");
      const privateKey = await crypto.decryptPrivateKeyWithPassword(
        encryptedPrivateKey,
        privateKeyEnvelope,
        oldPassword,
      );

      console.log("[Crypto] Old password verified successfully");

      // Step 3: Re-encrypt private key with NEW password
      console.log("[Crypto] Re-encrypting private key with new password...");
      const {
        encryptedPrivateKey: newEncryptedPrivateKey,
        envelope: newPrivateKeyEnvelope,
      } = await crypto.encryptPrivateKeyWithPassword(privateKey, newPassword);

      console.log("[Crypto] Private key re-encrypted successfully");
      console.log("[Crypto] New KDF:", newPrivateKeyEnvelope.kdf.algorithm);

      // Step 4: Send rotation request to backend
      const rotateRequest = {
        encryptedPrivateKey: newEncryptedPrivateKey,
        privateKeyEnvelope: newPrivateKeyEnvelope, // Pass as object, API will serialize
      };

      const rotateResponse = await rotateUserKey(oid, rotateRequest);

      if (rotateResponse.success) {
        console.log("[Crypto] Password rotation completed successfully");

        // Clear form and hide rotation form
        setOldPassword("");
        setNewPassword("");
        setNewPasswordConfirm("");
        setShowRotateForm(false);
        setError(null);

        // Show success banner
        setRotationSuccess(true);

        // Auto-hide success banner after 8 seconds
        setTimeout(() => {
          setRotationSuccess(false);
        }, 8000);
      } else {
        setError(
          rotateResponse.message || t("Fehler beim Ändern des Benutzer-Schlüssels"),
        );
      }
    } catch (err) {
      console.error("Error rotating password:", err);

      // Check for crypto errors (wrong old password)
      if (err.message && err.message.includes("Incorrect password")) {
        setError(t("Alter Benutzer-Schlüssel ist falsch"));
      } else {
        setError(t("Fehler beim Ändern des Benutzer-Schlüssels"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle MasterKey rotation (danger zone)
  const handleRotateMasterKey = async () => {
    setShowRotateConfirmModal(false);
    setRotating(true);
    setRotateSuccess(false);
    setError(null);

    try {
      // 1. Fetch all users with access (including public keys)
      const usersResponse = await getUsersForRotation(
        oid,
        passwortDatenbankId,
      );
      if (!usersResponse.success) {
        setError(usersResponse.message || t("Fehler beim Laden der Benutzer"));
        return;
      }
      const usersWithAccess = usersResponse.data;

      // 2. Fetch all current recipient passwords
      const currentPasswords = await getRecipientPasswords(
        oid,
        passwortDatenbankId,
      );

      // 3. Decrypt all passwords with OLD masterKey
      const decryptedPasswords = [];
      for (const pwd of currentPasswords) {
        const decrypted = await crypto.decryptRecipientPassword(
          pwd.encryptedEmpfaengerEmail,
          pwd.encryptedPasswort,
          pwd.iv,
          masterKey,
        );
        decryptedPasswords.push({
          empfaengerPasswortId: pwd.empfaengerPasswortId,
          email: decrypted.email,
          password: decrypted.password,
        });
      }

      // 4. Generate NEW masterKey
      const newMasterKey = crypto.generateMasterKey();

      // 5. Re-encrypt all passwords with NEW masterKey
      const reEncryptedPasswords = [];
      for (const dp of decryptedPasswords) {
        const encrypted = await crypto.encryptRecipientPassword(
          dp.email,
          dp.password,
          newMasterKey,
        );
        reEncryptedPasswords.push({
          empfaengerPasswortId: dp.empfaengerPasswortId,
          encryptedEmpfaengerEmail: encrypted.encryptedEmail,
          encryptedPasswort: encrypted.encryptedPassword,
          iv: encrypted.iv,
        });
      }

      // 6. Encrypt NEW masterKey for each user with their public key
      const newMasterKeyVersion = (masterKeyVersion || 1) + 1;
      const userMasterKeys = [];
      for (const user of usersWithAccess) {
        const encMk = await crypto.encryptMasterKeyWithPublicKey(
          newMasterKey,
          user.publicKey,
        );
        const envelope = crypto.createMasterKeyEnvelope(1, newMasterKeyVersion);
        userMasterKeys.push({
          benutzerId: user.benutzerId,
          encryptedMasterKey: encMk,
          masterKeyEnvelope: JSON.stringify(envelope),
        });
      }

      // 7. Call rotate endpoint
      const response = await rotateMasterKey({
        ownerOid: oid,
        passwortDatenbankId: passwortDatenbankId,
        expectedMasterKeyVersion: masterKeyVersion || 1,
        recipientPasswords: reEncryptedPasswords,
        userMasterKeys: userMasterKeys,
      });

      if (response.success) {
        // 8. Update local state with new masterKey
        if (onMasterKeyRotated) {
          onMasterKeyRotated(newMasterKey, newMasterKeyVersion);
        }
        setRotateSuccess(true);
        setTimeout(() => setRotateSuccess(false), 8000);
      } else if (response.statusCode === 409) {
        // Concurrency conflict - delegate to parent modal with countdown
        if (onConflictDetected) {
          onConflictDetected();
        }
      } else {
        setError(response.message || t("Fehler beim Ändern des MasterKeys"));
      }
    } catch (err) {
      console.error("[SecurityPage] MasterKey rotation error:", err);
      setError(t("Fehler beim Ändern des MasterKeys: {message}", {
        message: err.message || t("Unbekannter Fehler"),
      }));
    } finally {
      setRotating(false);
    }
  };

  // Handle password manager reset (danger zone)
  const handleResetPasswordManager = async () => {
    setSubmitting(true);
    setError(null);

    try {
      console.log("[Reset] Resetting password manager...");

      const response = await resetPasswordManager(oid);

      if (response.success) {
        console.log("[Reset] Password manager reset successfully");

        // Tell sidebar to clear crypto state
        try {
          const { sendRequest } = await import("../../services/dialogBridge");
          const { OPS } = await import("../../services/messageOps");
          await sendRequest(OPS.LOCK_REQUEST);
        } catch (bridgeErr) {
          console.warn("[SecurityPage] Failed to send LOCK_REQUEST to sidebar:", bridgeErr);
        }

        // Close modal
        setShowResetConfirmModal(false);

        // Reload security state (should now show initial setup screen)
        await loadSecurityState();

        // Reset local state
        setIsUnlocked(false);
        setPassword("");
        setPasswordConfirm("");
        setOldPassword("");
        setNewPassword("");
        setNewPasswordConfirm("");
        setShowRotateForm(false);

        // Success notification will be handled by state transition
      } else {
        setError(
          response.message || t("Fehler beim Zurücksetzen des Passwort-Managers"),
        );
        setShowResetConfirmModal(false);
      }
    } catch (err) {
      console.error("Error resetting password manager:", err);
      setError(t("Fehler beim Zurücksetzen des Passwort-Managers"));
      setShowResetConfirmModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle email update (Konto section)
  const handleUpdateEmail = async () => {
    if (!emailDraft || !emailDraft.trim()) {
      setError(t("Bitte geben Sie eine E-Mail-Adresse ein"));
      return;
    }

    setEmailSaving(true);
    setError(null);

    try {
      const response = await updateEmail(oid, emailDraft.trim());
      if (response.success) {
        setEmail(emailDraft.trim());
        setEditingEmail(false);
        setEmailSuccess(true);
        setTimeout(() => setEmailSuccess(false), 5000);
      } else {
        setError(response.message || t("Fehler beim Aktualisieren der E-Mail"));
      }
    } catch (err) {
      console.error("Error updating email:", err);
      setError(t("Fehler beim Aktualisieren der E-Mail-Adresse"));
    } finally {
      setEmailSaving(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div>
        <h2>{t("Sicherheit & Zugriff")}</h2>
        <p>{t("Laden...")}</p>
      </div>
    );
  }

  // Render error state
  if (!securityState) {
    return (
      <div>
        <h2>{t("Sicherheit & Zugriff")}</h2>
        {error && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fee",
              color: "#c00",
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}
        <button onClick={loadSecurityState}>{t("Erneut versuchen")}</button>
      </div>
    );
  }

  // STATE 1: Password Manager Deactivated (but user has key)
  // User deactivated PM and wants to reactivate → show unlock form with info message
  if (!securityState.passwortManagerAktiv && securityState.hasUserKey) {
    return (
      <div>
        <h2>{t("Passwortdatenbank reaktivieren")}</h2>
        <div
          style={{
            padding: "20px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          <p style={{ margin: 0 }}>
            ℹ️ {t("Der Passwort-Manager ist derzeit deaktiviert. Entsperren Sie Ihren Benutzer-Schlüssel, um ihn zu reaktivieren.")}
          </p>
        </div>
        <p style={{ color: "#666", marginBottom: "20px" }}>
          {t("Geben Sie Ihren Benutzer-Schlüssel ein, um die Passwortdatenbank zu reaktivieren.")}
        </p>

        {error && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fee",
              color: "#c00",
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleUnlock}>
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              {t("Benutzer-Schlüssel")}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("Ihr Passwort")}
              disabled={submitting}
              style={{
                width: "100%",
                maxWidth: "400px",
                padding: "8px",
                fontSize: "14px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: "10px 20px",
              backgroundColor: "#0078d4",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            {submitting ? t("Wird entsperrt...") : t("Entsperren und reaktivieren")}
          </button>
        </form>

        <p style={{ marginTop: "20px", fontSize: "12px", color: "#999" }}>
          {t("Die Entschlüsselung erfolgt client-seitig. Ihr Passwort wird niemals an den Server gesendet.")}
        </p>

        {/* Gefahrenbereich Section - Also show in reactivation state */}
        <div
          style={{
            marginTop: "48px",
            paddingTop: "24px",
            borderTop: "1px solid #ddd",
          }}
        >
          <h3 style={{ marginBottom: "16px", color: "#d32f2f" }}>
            Gefahrenbereich
          </h3>

          <div style={{ marginBottom: "20px" }}>
            <h4
              style={{
                marginBottom: "8px",
                fontSize: "15px",
                fontWeight: "600",
              }}
            >
              Passwort-Manager zurücksetzen
            </h4>
            <button
              onClick={() => setShowResetConfirmModal(true)}
              style={{
                padding: "8px 16px",
                backgroundColor: "#d32f2f",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              Zurücksetzen
            </button>
          </div>

          <div style={{ fontSize: "14px", color: "#666" }}>
            <p style={{ margin: "0 0 8px 0" }}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowResetConfirmModal(true);
                }}
                style={{
                  color: "#666",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Benutzer-Schlüssel vergessen?
              </a>
            </p>
            <p style={{ margin: 0, fontSize: "13px" }}>
              Wenn Sie Ihren Benutzer-Schlüssel nicht mehr kennen, können Sie
              den Passwort-Manager zurücksetzen und neu einrichten.
            </p>
          </div>
        </div>

        {/* Reset Confirmation Modal */}
        {showResetConfirmModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={() => !submitting && setShowResetConfirmModal(false)}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "24px",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{ marginTop: 0, marginBottom: "16px", color: "#d32f2f" }}
              >
                Passwort-Manager zurücksetzen
              </h3>

              <div
                style={{
                  marginBottom: "20px",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                <p style={{ margin: "0 0 4px 0", fontWeight: "bold", color: "#212121" }}>
                  ⚠️ Diese Aktion kann nicht rückgängig gemacht werden!
                </p>
                <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                  <li style={{ marginBottom: "8px" }}>
                    Dabei werden alle kryptografischen Schlüssel zurückgesetzt.
                  </li>
                  <li>
                    Alle bestehenden Passwortdatenbanken und Zugriffe können
                    danach nicht mehr genutzt werden.
                  </li>
                </ul>
              </div>

              {error && (
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#fee",
                    color: "#c00",
                    borderRadius: "4px",
                    marginBottom: "16px",
                    fontSize: "14px",
                  }}
                >
                  {error}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => setShowResetConfirmModal(false)}
                  disabled={submitting}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    fontSize: "14px",
                  }}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleResetPasswordManager}
                  disabled={submitting}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
            {submitting ? t("Wird zurückgesetzt...") : t("Ja, zurücksetzen")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // STATE 1b: Password Manager Deactivated AND no user key (first time ever)
  // Show setup form to create user key
  if (!securityState.passwortManagerAktiv && !securityState.hasUserKey) {
    return (
      <div>
        <h2>{t("Benutzer-Schlüssel festlegen")}</h2>
        <div
          style={{
            padding: "20px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          <p style={{ margin: 0 }}>
            ℹ️ {t("Der Passwort-Manager ist derzeit deaktiviert. Legen Sie einen Benutzer-Schlüssel fest, um ihn zu aktivieren.")}
          </p>
        </div>
        <p style={{ color: "#666", marginBottom: "20px" }}>
          {t("Legen Sie einen sicheren Benutzer-Schlüssel fest. Dieser wird verwendet, um Ihre privaten Schlüssel zu verschlüsseln.")}
        </p>

        {error && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fee",
              color: "#c00",
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSetupUserKey}>
          {/* Email field (prefilled, editable) */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              {t("E-Mail-Adresse")} <span style={{ color: "#c00" }}>*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("ihre.email@beispiel.de")}
              disabled={submitting}
              style={{
                width: "100%",
                maxWidth: "400px",
                padding: "8px",
                fontSize: "14px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
            <p
              style={{
                margin: "6px 0 0 0",
                fontSize: "13px",
                color: "#856404",
              }}
            >
              {t("Bitte überprüfen Sie Ihre E-Mail-Adresse. Diese wird für die Zugriffsverwaltung verwendet.")}
            </p>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              {t("Benutzer-Schlüssel")}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("Mindestens 8 Zeichen")}
              disabled={submitting}
              style={{
                width: "100%",
                maxWidth: "400px",
                padding: "8px",
                fontSize: "14px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              {t("Bestätigung")}
            </label>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder={t("Passwort wiederholen")}
              disabled={submitting}
              style={{
                width: "100%",
                maxWidth: "400px",
                padding: "8px",
                fontSize: "14px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </div>

          <div
            style={{
              padding: "16px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "4px",
              marginBottom: "16px",
              maxWidth: "500px",
            }}
          >
            <p style={{ margin: "0 0 8px 0", fontWeight: "600" }}>
              ⚠️ {t("Wichtiger Hinweis")}
            </p>
            <p style={{ margin: "0 0 12px 0", fontSize: "14px" }}>
              {t("Ihr Benutzer-Schlüssel kann nicht wiederhergestellt werden. Wenn Sie ihn vergessen, verlieren Sie den Zugriff auf Ihre verschlüsselten Daten.")}
            </p>
            <label
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <input
                type="checkbox"
                checked={agreedToWarning}
                onChange={(e) => setAgreedToWarning(e.target.checked)}
                disabled={submitting}
              />
              <span style={{ fontSize: "14px" }}>
                {t("Ich habe verstanden und akzeptiere das Risiko")}
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting || !agreedToWarning}
            style={{
              padding: "10px 20px",
              backgroundColor: agreedToWarning ? "#0078d4" : "#ccc",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: agreedToWarning ? "pointer" : "not-allowed",
              fontSize: "14px",
            }}
            >
            {submitting
              ? t("Wird eingerichtet...")
              : t("Passwortdatenbank aktivieren")}
          </button>
        </form>

        <p style={{ marginTop: "20px", fontSize: "12px", color: "#999" }}>
          {t("Hinweis: Die Verschlüsselung erfolgt client-seitig. Ihr Benutzer-Schlüssel wird niemals an den Server gesendet.")}
        </p>
      </div>
    );
  }

  // STATE 4: Unlocked (user navigated back from Passwörter page)
  if (isUnlocked) {
    return (
      <div>
        <h2>{t("Sicherheit & Zugriff")}</h2>

        {/* Success Banner for Password Rotation */}
        {rotationSuccess && (
          <div
            style={{
              padding: "16px",
              backgroundColor: "#d4edda",
              border: "1px solid #28a745",
              borderRadius: "4px",
              marginTop: "16px",
              marginBottom: "16px",
            }}
          >
            <p style={{ margin: 0, fontWeight: "600", color: "#155724" }}>
              ✅ {t("Benutzer-Schlüssel erfolgreich geändert!")}
            </p>
            <p
              style={{
                margin: "8px 0 0 0",
                fontSize: "14px",
                color: "#155724",
              }}
            >
              {t("Ihr neues Passwort wird ab jetzt für das Entsperren verwendet.")}
            </p>
          </div>
        )}

        {/* Notification Banner */}
        {notifications.length > 0 && (
          <div
            style={{
              marginTop: "16px",
              marginBottom: "16px",
              padding: "20px",
              backgroundColor: "#fff8e1",
              border: "1px solid #ffca28",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "12px",
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", color: "#5d4037" }}>
                  {t("Wichtige Benachrichtigungen ({count})", { count: notifications.length })}
                </h3>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "13px",
                    color: "#795548",
                  }}
                >
                  {t("Bitte prüfen, bevor Sie fortfahren.")}
                </p>
              </div>
              <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                {notifications.length > 1 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "transparent",
                      color: "#0078d4",
                      border: "1px solid #0078d4",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("Alle als gelesen markieren")}
                  </button>
                )}
                <button
                  onClick={() => onNavigate && onNavigate("passwords")}
                  style={{
                    padding: "6px 16px",
                    backgroundColor: "#0078d4",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("Weiter zu Passwörter")}
                </button>
              </div>
            </div>
            {notifications.map((notification) => {
              const style = getNotificationStyle(notification.typName);
              return (
                <div
                  key={notification.benachrichtigungId}
                  style={{
                    padding: "12px 16px",
                    backgroundColor: style.bgColor,
                    border: `1px solid ${style.borderColor}`,
                    borderRadius: "4px",
                    marginBottom: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <span style={{ marginRight: "8px" }}>{style.icon}</span>
                    <span style={{ color: style.color, fontSize: "14px" }}>
                      {notification.nachricht}
                    </span>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginTop: "4px",
                      }}
                    >
                      {formatDateTime(notification.erstelltAm)}
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      handleMarkAsRead(notification.benachrichtigungId)
                    }
                    title={t("Als gelesen markieren")}
                    style={{
                      padding: "4px 8px",
                      backgroundColor: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "16px",
                      color: "#888",
                      marginLeft: "12px",
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Two-column layout: Benutzer-Schlüssel + Konto */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "32px",
            marginTop: "24px",
          }}
        >
          {/* Password Rotation Section */}
          <div style={{ flex: "1 1 300px", minWidth: "280px" }}>
            <h3 style={{ marginBottom: "16px" }}>{t("Benutzer-Schlüssel")}</h3>

            {!showRotateForm ? (
              <div style={{ marginBottom: "12px" }}>
                {/* Spacer to align with Konto section (simulates label height) */}
                <div style={{ height: "20px", marginBottom: "6px" }}></div>
                <p
                  style={{
                    margin: "0 0 10px 0",
                    fontSize: "14px",
                    color: "#333",
                  }}
                >
                  ••••••••••••
                </p>
                <button
                  onClick={() => {
                    setShowRotateForm(true);
                    setEditingEmail(false);
                    setEmailDraft("");
                    setError(null);
                  }}
                  style={{
                    width: "220px",
                    padding: "8px 16px",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {t("Benutzer-Schlüssel ändern")}
                </button>
              </div>
            ) : (
              <div>
                <p
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    marginBottom: "16px",
                  }}
                >
                  {t("Ändern Sie Ihren Benutzer-Schlüssel. Der Private Key wird mit dem neuen Benutzer-Schlüssel verschlüsselt.")}
                </p>

                {error && (
                  <div
                    style={{
                      padding: "12px",
                      backgroundColor: "#fee",
                      color: "#c00",
                      borderRadius: "4px",
                      marginBottom: "16px",
                    }}
                  >
                    {error}
                  </div>
                )}

                <form onSubmit={handleRotatePassword}>
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      {t("Alter Benutzer-Schlüssel")}
                    </label>
                    <input
                      ref={oldPasswordRef}
                      type="password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      placeholder={t("Aktueller Benutzer-Schlüssel")}
                      disabled={submitting}
                      style={{
                        width: "100%",
                        maxWidth: "400px",
                        padding: "8px",
                        fontSize: "14px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      {t("Neuer Benutzer-Schlüssel")}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t("Mindestens 8 Zeichen")}
                      disabled={submitting}
                      style={{
                        width: "100%",
                        maxWidth: "400px",
                        padding: "8px",
                        fontSize: "14px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                      }}
                    >
                      {t("Neuen Benutzer-Schlüssel bestätigen")}
                    </label>
                    <input
                      type="password"
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      placeholder={t("Neuen Benutzer-Schlüssel wiederholen")}
                      disabled={submitting}
                      style={{
                        width: "100%",
                        maxWidth: "400px",
                        padding: "8px",
                        fontSize: "14px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  </div>

                  <div style={{ display: "flex", gap: "12px" }}>
                    <button
                      type="submit"
                      disabled={submitting}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#0078d4",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: submitting ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      {submitting
                        ? t("Wird geändert...")
                        : t("Benutzer-Schlüssel ändern")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRotateForm(false);
                        setOldPassword("");
                        setNewPassword("");
                        setNewPasswordConfirm("");
                        setError(null);
                      }}
                      disabled={submitting}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#6c757d",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: submitting ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Abbrechen
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Konto Section */}
          <div style={{ flex: "1 1 300px", minWidth: "280px" }}>
            <h3 style={{ marginBottom: "16px" }}>{t("Konto")}</h3>

            {emailSuccess && (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#d4edda",
                  border: "1px solid #28a745",
                  borderRadius: "4px",
                  marginBottom: "16px",
                  color: "#155724",
                  fontSize: "14px",
                }}
              >
                {t("E-Mail-Adresse erfolgreich aktualisiert.")}
              </div>
            )}

            <div style={{ marginBottom: "12px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                {t("E-Mail-Adresse")}
              </label>

              {!editingEmail ? (
                <div>
                  <p
                    style={{
                      margin: "0 0 10px 0",
                      fontSize: "14px",
                      color: "#333",
                    }}
                  >
                    {email || t("Nicht gesetzt")}
                  </p>
                  <button
                    onClick={() => {
                      setEditingEmail(true);
                      setEmailDraft(email);
                      setShowRotateForm(false);
                      setOldPassword("");
                      setNewPassword("");
                      setNewPasswordConfirm("");
                      setError(null);
                    }}
                    style={{
                      width: "220px",
                      padding: "8px 16px",
                      backgroundColor: "#6c757d",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    {t("E-Mail-Adresse ändern")}
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    ref={emailDraftRef}
                    type="email"
                    value={emailDraft}
                    onChange={(e) => setEmailDraft(e.target.value)}
                    placeholder={t("ihre.email@beispiel.de")}
                    disabled={emailSaving}
                    style={{
                      width: "100%",
                      maxWidth: "400px",
                      padding: "8px",
                      fontSize: "14px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      marginBottom: "10px",
                      display: "block",
                    }}
                  />
                  <div style={{ display: "flex", gap: "12px" }}>
                    <button
                      onClick={handleUpdateEmail}
                      disabled={emailSaving}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#0078d4",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: emailSaving ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      {emailSaving ? t("Speichern...") : t("Speichern")}
                    </button>
                    <button
                      onClick={() => {
                        setEditingEmail(false);
                        setEmailDraft("");
                        setError(null);
                      }}
                      disabled={emailSaving}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#6c757d",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: emailSaving ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* End two-column layout */}

        {/* Gefahrenbereich Section */}
        <div
          style={{
            marginTop: "48px",
            paddingTop: "24px",
            borderTop: "1px solid #ddd",
          }}
        >
          <h3 style={{ marginBottom: "16px", color: "#d32f2f" }}>
            {t("Gefahrenbereich")}
          </h3>

          {/* MasterKey rotation success banner (full width, above the row) */}
          {isOwner && rotateSuccess && (
            <div
              style={{
                padding: "10px 12px",
                marginBottom: "16px",
                borderRadius: "4px",
                fontSize: "13px",
                backgroundColor: "#d4edda",
                border: "1px solid #c3e6cb",
                color: "#155724",
              }}
            >
              {t("MasterKey wurde erfolgreich geändert.")}
            </div>
          )}

          {/* Two-column layout: Zurücksetzen + MasterKey ändern */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "32px",
              marginBottom: "20px",
            }}
          >
            {/* Passwort-Manager zurücksetzen */}
            <div style={{ flex: "1 1 300px", minWidth: "280px" }}>
              <h4
                style={{
                  marginBottom: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                }}
              >
                {t("Passwort-Manager zurücksetzen")}
              </h4>
              <p
                style={{
                  fontSize: "13px",
                  color: "#666",
                  marginBottom: "12px",
                  marginTop: 0,
                }}
              >
                {t("Setzt den Passwort-Manager vollständig zurück und entfernt Schlüssel sowie Zugriffe. Anschließend müssen Sie ihn neu einrichten.")}
              </p>
              <button
                onClick={() => setShowResetConfirmModal(true)}
                style={{
                  width: "220px",
                  padding: "8px 16px",
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {t("Zurücksetzen")}
              </button>
            </div>

            {/* MasterKey rotation (Owner only) */}
            {isOwner && (
              <div style={{ flex: "1 1 300px", minWidth: "280px" }}>
                <h4
                  style={{
                    marginBottom: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                  }}
                >
                  {t("MasterKey ändern")}
                </h4>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#666",
                    marginBottom: "12px",
                    marginTop: 0,
                  }}
                >
                  {t("Verschlüsselt alle Einträge neu mit einem neuen MasterKey.")}
                  <br />&nbsp;
                </p>
                <button
                  onClick={() => setShowRotateConfirmModal(true)}
                  disabled={rotating}
                  style={{
                    width: "220px",
                    padding: "8px 16px",
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: rotating ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {rotating ? t("Wird geändert...") : t("MasterKey ändern")}
                </button>
              </div>
            )}
          </div>

          <div style={{ fontSize: "14px", color: "#666" }}>
            <p style={{ margin: "0 0 8px 0" }}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowResetConfirmModal(true);
                }}
                style={{
                  color: "#666",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {t("Benutzer-Schlüssel vergessen?")}
              </a>
            </p>
            <p style={{ margin: 0, fontSize: "13px" }}>
              {t("Wenn Sie Ihren Benutzer-Schlüssel nicht mehr kennen, können Sie den Passwort-Manager zurücksetzen und neu einrichten.")}
            </p>
          </div>
        </div>

        {/* Reset Confirmation Modal */}
        {showResetConfirmModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={() => !submitting && setShowResetConfirmModal(false)}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "24px",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{ marginTop: 0, marginBottom: "16px", color: "#d32f2f" }}
              >
                {t("Passwort-Manager zurücksetzen")}
              </h3>

              <div
                style={{
                  marginBottom: "20px",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                <p style={{ margin: "0 0 4px 0", fontWeight: "bold", color: "#212121" }}>
                  ⚠️ {t("Diese Aktion kann nicht rückgängig gemacht werden!")}
                </p>
                <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                  <li style={{ marginBottom: "8px" }}>
                    {t("Dabei werden alle kryptografischen Schlüssel zurückgesetzt.")}
                  </li>
                  <li>
                    {t("Alle bestehenden Passwortdatenbanken und Zugriffe können danach nicht mehr genutzt werden.")}
                  </li>
                </ul>
              </div>

              {error && (
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#fee",
                    color: "#c00",
                    borderRadius: "4px",
                    marginBottom: "16px",
                    fontSize: "14px",
                  }}
                >
                  {error}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => setShowResetConfirmModal(false)}
                  disabled={submitting}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    fontSize: "14px",
                  }}
                >
                  {t("Abbrechen")}
                </button>
                <button
                  onClick={handleResetPasswordManager}
                  disabled={submitting}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {submitting ? t("Wird zurückgesetzt...") : t("Ja, zurücksetzen")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MasterKey Rotation Confirmation Modal */}
        {showRotateConfirmModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={() => !rotating && setShowRotateConfirmModal(false)}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "24px",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "16px",
                  color: "#d32f2f",
                }}
              >
                {t("MasterKey ändern?")}
              </h3>

              <div
                style={{
                  marginBottom: "20px",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                <p style={{ margin: "0 0 4px 0", fontWeight: "bold", color: "#212121" }}>
                  ⚠️ {t("Dieser Vorgang kann nicht rückgängig gemacht werden!")}
                </p>
                <p style={{ margin: "8px 0 0 0" }}>
                  {t("Alle Einträge werden neu verschlüsselt.")}
                </p>
              </div>

              {error && (
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#fee",
                    color: "#c00",
                    borderRadius: "4px",
                    marginBottom: "16px",
                    fontSize: "14px",
                  }}
                >
                  {error}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => setShowRotateConfirmModal(false)}
                  disabled={rotating}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: rotating ? "not-allowed" : "pointer",
                    fontSize: "14px",
                  }}
                >
                  {t("Abbrechen")}
                </button>
                <button
                  onClick={handleRotateMasterKey}
                  disabled={rotating}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: rotating ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {rotating ? t("Wird geändert...") : t("Ja, MasterKey ändern")}
                </button>
              </div>
            </div>
          </div>
        )}

        <p style={{ marginTop: "24px", color: "#666", fontSize: "14px" }}>
          {t("Verwenden Sie das Menü links, um zu den Passwörtern oder zur Zugriffsverwaltung zu navigieren.")}
        </p>
      </div>
    );
  }

  // STATE 2: User Key Setup (First Time)
  if (!securityState.hasUserKey) {
    return (
      <div>
        <h2>{t("Benutzer-Schlüssel festlegen")}</h2>
        <p style={{ color: "#666", marginBottom: "20px" }}>
          {t("Legen Sie einen sicheren Benutzer-Schlüssel fest. Dieser wird verwendet, um Ihre privaten Schlüssel zu verschlüsseln.")}
        </p>

        {error && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fee",
              color: "#c00",
              borderRadius: "4px",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSetupUserKey}>
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              {t("Benutzer-Schlüssel")}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("Mindestens 8 Zeichen")}
              disabled={submitting}
              style={{
                width: "100%",
                maxWidth: "400px",
                padding: "8px",
                fontSize: "14px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              {t("Bestätigung")}
            </label>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder={t("Passwort wiederholen")}
              disabled={submitting}
              style={{
                width: "100%",
                maxWidth: "400px",
                padding: "8px",
                fontSize: "14px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
          </div>

          <div
            style={{
              padding: "16px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "4px",
              marginBottom: "16px",
              maxWidth: "500px",
            }}
          >
            <p style={{ margin: "0 0 8px 0", fontWeight: "600" }}>
              ⚠️ {t("Wichtiger Hinweis")}
            </p>
            <p style={{ margin: "0 0 12px 0", fontSize: "14px" }}>
              {t("Ihr Benutzer-Schlüssel kann nicht wiederhergestellt werden. Wenn Sie ihn vergessen, verlieren Sie den Zugriff auf Ihre verschlüsselten Daten.")}
            </p>
            <label
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <input
                type="checkbox"
                checked={agreedToWarning}
                onChange={(e) => setAgreedToWarning(e.target.checked)}
                disabled={submitting}
              />
              <span style={{ fontSize: "14px" }}>
                {t("Ich habe verstanden und akzeptiere das Risiko")}
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting || !agreedToWarning}
            style={{
              padding: "10px 20px",
              backgroundColor: agreedToWarning ? "#0078d4" : "#ccc",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: agreedToWarning ? "pointer" : "not-allowed",
              fontSize: "14px",
            }}
          >
            {submitting
              ? t("Wird eingerichtet...")
              : t("Benutzer-Schlüssel festlegen")}
          </button>
        </form>

        <p style={{ marginTop: "20px", fontSize: "12px", color: "#999" }}>
          {t("Hinweis: Die Verschlüsselung erfolgt client-seitig. Ihr Benutzer-Schlüssel wird niemals an den Server gesendet.")}
        </p>
      </div>
    );
  }

  // STATE 3: Unlock (Enter Password)
  return (
    <div>
      <h2>{t("Passwortdatenbank entsperren")}</h2>
      <p style={{ color: "#666", marginBottom: "20px" }}>
        {t("Geben Sie Ihren Benutzer-Schlüssel ein, um auf Ihre verschlüsselten Passwörter zuzugreifen.")}
      </p>

      {error && (
        <div
          style={{
            padding: "12px",
            backgroundColor: "#fee",
            color: "#c00",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleUnlock}>
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "600",
            }}
          >
            {t("Benutzer-Schlüssel")}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("Ihr Benutzer-Schlüssel")}
            disabled={submitting}
            autoFocus
            style={{
              width: "100%",
              maxWidth: "400px",
              padding: "8px",
              fontSize: "14px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        </div>

        <button
          type="submit"
          disabled={submitting || !password}
          style={{
            padding: "10px 20px",
            backgroundColor: password ? "#0078d4" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: password ? "pointer" : "not-allowed",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          {submitting ? t("Wird entsperrt...") : t("Entsperren")}
        </button>
      </form>

      <p style={{ marginTop: "20px", fontSize: "12px", color: "#999" }}>
        {t("Die Entschlüsselung erfolgt client-seitig. Ihr Passwort wird niemals an den Server gesendet.")}
      </p>

      {/* Gefahrenbereich Section - Also show in unlock state */}
      <div
        style={{
          marginTop: "48px",
          paddingTop: "24px",
          borderTop: "1px solid #ddd",
        }}
      >
        <h3 style={{ marginBottom: "16px", color: "#d32f2f" }}>
          {t("Gefahrenbereich")}
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ marginBottom: "8px", fontSize: "15px", fontWeight: "600" }}
          >
            {t("Passwort-Manager zurücksetzen")}
          </h4>
          <button
            onClick={() => setShowResetConfirmModal(true)}
            style={{
              padding: "8px 16px",
              backgroundColor: "#d32f2f",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            {t("Zurücksetzen")}
          </button>
        </div>

        <div style={{ fontSize: "14px", color: "#666" }}>
          <p style={{ margin: "0 0 8px 0" }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowResetConfirmModal(true);
              }}
              style={{
                color: "#666",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {t("Benutzer-Schlüssel vergessen?")}
            </a>
          </p>
          <p style={{ margin: 0, fontSize: "13px" }}>
            {t("Wenn Sie Ihren Benutzer-Schlüssel nicht mehr kennen, können Sie den Passwort-Manager zurücksetzen und neu einrichten.")}
          </p>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirmModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => !submitting && setShowResetConfirmModal(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{ marginTop: 0, marginBottom: "16px", color: "#d32f2f" }}
            >
              {t("Passwort-Manager zurücksetzen")}
            </h3>

            <div
              style={{
                marginBottom: "20px",
                fontSize: "14px",
                lineHeight: "1.6",
              }}
            >
              <p style={{ margin: "0 0 4px 0", fontWeight: "bold", color: "#212121" }}>
                ⚠️ {t("Diese Aktion kann nicht rückgängig gemacht werden!")}
              </p>
              <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                <li style={{ marginBottom: "8px" }}>
                  {t("Dabei werden alle kryptografischen Schlüssel zurückgesetzt.")}
                </li>
                <li>
                  {t("Alle bestehenden Passwortdatenbanken und Zugriffe können danach nicht mehr genutzt werden.")}
                </li>
              </ul>
            </div>

            {error && (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#fee",
                  color: "#c00",
                  borderRadius: "4px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                {error}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowResetConfirmModal(false)}
                disabled={submitting}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: submitting ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleResetPasswordManager}
                disabled={submitting}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: submitting ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {submitting ? "Wird zurückgesetzt..." : "Ja, zurücksetzen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
